import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Button } from '@material-ui/core';
import {
  compose, filter, first, get, map, size,
} from 'lodash/fp';

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
    width: 70%;
    border: 1px solid ${({ theme }) => theme.palette.grey[200]};
    border-bottom-color: ${({ theme }) => theme.palette.grey[300]};
    border-radius: 4px;
    ${({ active, theme }) => active === 0 && css`
      border-left-color: ${theme.palette.grey[300]};
    `}
    ${({ active, theme }) => active === 1 && css`
      border-right-color: ${theme.palette.grey[300]};
    `}
  `,
  Tabs: styled.ul`
    position: relative;
    top: -1px;
    left: -1px;
    display: flex;
    width: calc(100% + 2px);
  `,
  Tab: styled.li`
    position: relative;
    flex-basis: 50%;
    border-radius: 4px;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    ::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      border-bottom: 1px;
      border-radius: 4px;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
    ${({ active, left, theme }) => active && css`
      ::before {
        border-top: 1px solid ${theme.palette.grey[300]};
        border-left: ${!left && `1px solid ${theme.palette.grey[300]}`};
        border-right: ${left && `1px solid ${theme.palette.grey[300]}`};
      }
    `}
  `,
  Button: styled(({ active, ...otherProps }) => <Button {...otherProps} />)`
    position: relative;
    width: 100%;
    min-height: 48px;
    border-radius: 0;
    line-height: 1.53;
    :hover {
      background: transparent;
    }
    ${({ active, theme }) => active && css`
      color: ${theme.palette.grey[800]};
      font-weight: 700;
    `}
  `,
  Data: styled.data`
    margin-left: 4px;
  `,
  Content: styled.div`
    position: relative;
    left: -1px;
    flex-grow: 1;
    width: calc(100% + 2px);
    /* TODO: 고정 높이를 유동 높이로 변경 요망 */
    height: 242px;
    ::before {
      content: '';
      position: absolute;
      top: -1px;
      bottom: -1px;
      left: 0;
      width: calc(50% + 1px);
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    ::after {
      content: '';
      position: absolute;
      top: -1px;
      bottom: -1px;
      right: 0;
      width: calc(50% + 1px);
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
    ${({ active, theme }) => active === 0 && css`
      ::after {
        border-top: 1px solid ${theme.palette.grey[300]};
        border-right: 1px solid ${theme.palette.grey[300]};
      }
    `}
    ${({ active, theme }) => active === 1 && css`
      ::before {
        border-top: 1px solid ${theme.palette.grey[300]};
        border-left: 1px solid ${theme.palette.grey[300]};
      }
    `}
  `,
  Targets: styled.ul`
    position: relative;
    z-index: 1;
    overflow: auto;
    height: 100%;
    padding: 8px 24px;
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    line-height: 1.53;
  `,
  Target: styled.li`
    display: flex;
    align-items: center;
    min-height: 32px;
  `,
};

const TargetList = ({
  className, active, targets, switchTab,
}) => (
  <S.Wrapper className={className} active={active}>
    <S.Tabs>
      <S.Tab active={active === 0} left>
        <S.Button
          type="button"
          data-value="0"
          active={active === 0}
          onClick={switchTab}
          disableRipple
        >
          응답
          <S.Data
            value={size(filter(target => target.recorded, targets))}
          >
            {size(filter(target => target.recorded, targets))}
          </S.Data>
        </S.Button>
      </S.Tab>
      <S.Tab active={active === 1}>
        <S.Button
          type="button"
          data-value="1"
          active={active === 1}
          onClick={switchTab}
          disableRipple
        >
          미응답
          <S.Data value={size(filter(target => !target.recorded, targets))}>
            {size(filter(target => !target.recorded, targets))}
          </S.Data>
        </S.Button>
      </S.Tab>
    </S.Tabs>
    <S.Content active={active}>
      <S.Targets>
        {compose(
          map(target => (
            <S.Target key={target.cid || target.id}>
              {target.clazzGroup
                ? `${get('grade', target.clazzGroup)}학년 ${get('clazzName', target.clazzGroup)}반 ${get('clazzNumber', target)
                  ? `${get('clazzNumber', target)}번` : ''} ${target.name}`
                : `${get('name', first(target.customGroups)) || '미지정'} ${target.name || '-'}`}
            </S.Target>
          )),
          filter(active === 0 ? target => target.recorded : target => !target.recorded),
        )(targets)}
      </S.Targets>
    </S.Content>
  </S.Wrapper>
);

TargetList.propTypes = {
  className: PropTypes.string,
  active: PropTypes.number.isRequired,
  targets: PropTypes.arrayOf(PropTypes.shape({})),
  switchTab: PropTypes.func.isRequired,
};

TargetList.defaultProps = {
  className: undefined,
  targets: null,
};

export default TargetList;
